"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.DropdownPopover = void 0;
exports.useDropdownPopover = useDropdownPopover;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _defineProperty2 = _interopRequireDefault(require("@babel/runtime/helpers/defineProperty"));
var _objectWithoutProperties2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutProperties"));
var _react = _interopRequireDefault(require("react"));
var _classnames = _interopRequireDefault(require("classnames"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _disrupt = _interopRequireDefault(require("@ux/disrupt"));
var _flyout = _interopRequireDefault(require("@ux/flyout"));
var _stretch = _interopRequireDefault(require("@ux/flyout/middleware/stretch"));
var _flip = _interopRequireDefault(require("@ux/flyout/middleware/flip"));
var _mergeRefs = _interopRequireDefault(require("merge-refs"));
var _dropdown = require("./dropdown");
var _canUseDom = _interopRequireDefault(require("@ux/component-utilities/can-use-dom"));
var _composeEventHandlers = _interopRequireDefault(require("@ux/component-utilities/compose-event-handlers"));
const _excluded = ["onBlur", "onClose", "portal", "position", "ref"],
  _excluded2 = ["stretch", "flip"],
  _excluded3 = ["children", "show", "ref", "className"];
/* eslint-disable no-undefined */
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { (0, _defineProperty2.default)(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/**
 * @type PopoverHook
 * @property {object} data Derived data from Dropdown state
 * @property {HTMLElement} data.portal Portal element
 * @property {string} data.position Flyout position
 * @property {React.ForwardedRef} data.targetRef Trigger Element Ref
 * @property {boolean} data.isExpanded Is the menu open or closed
 * @property {boolean} data.isTouch When isTouch is set to true a Disrupt is used instead of a Flyout for containing the items
 * @property {object} props Props to be spread onto element
 * @property {React.ForwardedRef} props.ref Composed Ref
 * @property {boolean} props.show Should open or close menu
 * @property {function} props.onBlur Composed Blur handler
 * @property {function} props.onClose Composed Close Handler
 */
/**
 * Shares whether the menu popover, Flyout, should be open or not. Also, it dictates where to portal elements.
 * @param {Object} props useDropdownPopover props
 * @param {function} props.onBlur Blur event handler
 * @param {function} props.onClose Callback when the menu is closed
 * @param {HTMLElement} props.portal Specific portal element
 * @param {string} [props.position=bottom-start] Flyout position
 * @param {React.ForwardedRef} props.ref Forwarded Ref
 * @returns {PopoverHook} Data used to implement dropdown popover
 */
function useDropdownPopover(_ref) {
  let {
      onBlur,
      onClose,
      portal,
      position = 'bottom-start',
      ref: forwardedRef
    } = _ref,
    props = (0, _objectWithoutProperties2.default)(_ref, _excluded);
  const {
    isTouch,
    triggerRef,
    dispatch,
    popoverRef,
    state: {
      isExpanded
    }
  } = (0, _dropdown.useDropdownContext)('useDropdownPopover');
  const ref = (0, _mergeRefs.default)(popoverRef, forwardedRef);
  return {
    data: {
      portal,
      position,
      targetRef: triggerRef,
      isExpanded,
      isTouch
    },
    props: _objectSpread({
      ref,
      show: isExpanded,
      onBlur: (0, _composeEventHandlers.default)(onBlur, event => {
        if (event.currentTarget.contains(event.relatedTarget)) {
          return;
        }
        dispatch({
          type: _dropdown.CLOSE_MENU,
          payload: {
            triggerRef
          }
        });
      }),
      isTouch: false,
      onClose: (0, _composeEventHandlers.default)(onClose, () => {
        dispatch({
          type: _dropdown.CLOSE_MENU,
          payload: {
            triggerRef
          }
        });
      })
    }, props)
  };
}
const DropdownPopover = exports.DropdownPopover = /*#__PURE__*/_react.default.forwardRef((_ref2, forwardedRef) => {
  let {
      stretch: shouldStretch,
      flip: shouldFlip
    } = _ref2,
    rest = (0, _objectWithoutProperties2.default)(_ref2, _excluded2);
  const {
    data: {
      isTouch,
      portal,
      targetRef,
      position
    },
    props
  } = useDropdownPopover(_objectSpread(_objectSpread({}, rest), {}, {
    ref: forwardedRef
  }));

  // Disrupt and Flyout do not forwardRef
  // eslint-disable-next-line no-unused-vars
  const {
      children,
      show,
      ref,
      className
    } = props,
    sharedProps = (0, _objectWithoutProperties2.default)(props, _excluded3);
  if (!(0, _canUseDom.default)()) {
    return null;
  }
  if (show && isTouch) {
    return /*#__PURE__*/_react.default.createElement(_disrupt.default, (0, _extends2.default)({
      portalElem: portal,
      stretch: true
    }, sharedProps), children);
  }
  const cx = (0, _classnames.default)({
    [className]: !!className,
    'u-hide': !show
  });
  const middlewareProps = [];
  if (shouldStretch) middlewareProps.push((0, _stretch.default)());
  if (shouldFlip) middlewareProps.push((0, _flip.default)());
  return /*#__PURE__*/_react.default.createElement(_flyout.default, (0, _extends2.default)({
    className: cx,
    portalElem: portal,
    anchorRef: targetRef,
    placement: position,
    middleware: middlewareProps
  }, sharedProps), children);
});
DropdownPopover.propTypes = {
  children: _propTypes.default.node,
  className: _propTypes.default.string,
  isTouch: _propTypes.default.bool,
  ref: _propTypes.default.node,
  show: _propTypes.default.bool,
  stretch: _propTypes.default.bool,
  flip: _propTypes.default.bool
};
DropdownPopover.displayName = 'DropdownPopover';